/***
 * Creates a function that is restricted to invoking func once.
 * Repeat calls to the function return the value of the first invocation.
 * The func is invoked with the this binding and arguments of the created function.
 * @param func
 * @returns {T}
 */
export function once<T extends Function>(func: T): T {
  let called = false
  let res: any
  return (function(this: any) {
    if (!called) {
      called = true
      res = func.apply(this, arguments)
    }
    return res
  } as any) as T
}

/***
 *Creates a debounced function that delays invoking func until after wait milliseconds have elapsed
 * since the last time the debounced function was invoked.
 * The func is invoked with the last arguments provided to the debounced function.
 * @param func
 * @param wait
 * @returns {Function}
 */
export function debounce<T extends Function>(func: T, wait: number): T {
  let timeout: any
  return (function(this: any) {
    const context = this
    const args = arguments
    const later = function() {
      timeout = null
      func.apply(context, args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  } as any) as T
}

export function memoize<T extends (...args: any[]) => any>(
  func: T,
  resolver: (args: any[]) => string
): T {
  const cache: { [key: string]: any } = {}
  return function(...args: any[]) {
    let key = resolver(args)
    if (cache[key]) {
      return cache[key]
    } else {
      return (cache[key] = func.apply(null, args))
    }
  } as T
}
